import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Pencil, Trash2, AlarmClock, Sparkles, LayoutList } from 'lucide-react';
import i18n from '../../../i18n';
import { LiveTimerTag } from './LiveTimerTag';

// slice 02.1 · paso b.5d.1 — presentacional. Los 2 renders de la feed de comentarios/moments
// del panel de detalle de una tarea (era JSX inline de `ComponentTaskHoy`, dentro del `.map()`
// de `getTaskFilteredComments()`). Co-locados en un archivo (como `UserCell.jsx`, b.3a) porque
// `MomentItem` es chico y ambos sólo se usan desde `TaskCommentsPanel` (b.5d.2).
//
// Toda mutación/toast queda en el contenedor (boundary lint, `02.1-b5-detail-panel.md` §2):
// `onToggleMediaVisibility` recibe los nodos DOM (`wrapper`/`toggleBtn`) que ya tocaba el
// handler original -- es la misma lógica optimista de "parchear el DOM ya" documentada en el
// comentario de abajo, sólo que ahora la ejecuta el contenedor porque necesita `queryClient`/
// `commentsQueryKey`/`toast`. El slot `renderCommentEditor` compone `TaskCommentEditor` (modo
// edición) con todo su estado de borrador -- container-side, igual que `renderReminderPopover`.
export const MomentItem = ({ comment: c }) => {
    const { t: translate } = useTranslation('tasks');
    const m = c.metadata || {};
    let actionText = '';
    if (m.type === 'creation') actionText = translate('comments.creatorAction');
    else if (m.type === 'title_change') actionText = translate('comments.titleChangeAction', { oldValue: m.oldValue || '', newValue: m.newValue || '' });
    else if (m.type === 'description_change') actionText = translate('comments.descriptionChangeAction');
    else if (m.type === 'status_change') actionText = translate('comments.statusChangeAction', { oldValue: m.oldValue || '', newValue: m.newValue || '' });

    const isTimerState = m.type === 'status_change' && (['check', 'bloqueo', 'en proceso', 'retraso'].includes(m.newValue) || m.timerActive !== undefined);

    let tagBgColor = '#ca8a04';
    let tagTextColor = '#fef08a';
    if (m.newValue === 'bloqueo') { tagBgColor = '#b91c1c'; tagTextColor = '#fecaca'; }
    else if (m.newValue === 'retraso') { tagBgColor = '#c2410c'; tagTextColor = '#ffedd5'; }
    else if (m.newValue === 'en proceso') { tagBgColor = '#1d4ed8'; tagTextColor = '#dbeafe'; }

    return (
        <div className="moment-item" style={{ padding: 'var(--space-3) 0 var(--space-2) 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img 
                    src={c.author_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${c.author_name || 'U'}`} 
                    alt={c.author_name} 
                    loading="lazy" 
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${c.author_name || 'U'}`; }}
                    style={{ width: '16px', height: '16px', borderRadius: '50%' }} 
                />
                <span style={{ fontSize: '10px', fontWeight: 'normal', color: '#888' }}>{c.author_name}</span>
                <span style={{ fontSize: '10px', color: '#888', fontStyle: 'normal' }}>{actionText}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '10px' }}>
                <div style={{ fontSize: '10px', color: '#666', whiteSpace: 'nowrap' }}>
                    {new Date(c.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                </div>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }}></div>
                {isTimerState && (
                    <LiveTimerTag startTime={m.timerStart || c.created_at} active={m.timerActive} savedDuration={m.durationSaved || 0} bgColor={tagBgColor} textColor={tagTextColor} />
                )}
            </div>
        </div>
    );
};

export const CommentItem = ({
    comment: c,
    currentUserId,
    usersToSelectFrom = [],
    isEditing,
    renderCommentEditor,
    onToggleReaction,
    onStartEdit,
    onDeleteComment,
    onToggleMediaVisibility,
    onExpandImage,
    hoveredReminderId,
    hoveredReminderData,
    reminderTooltipPos,
    onReminderMouseEnter,
    onReminderMouseLeave,
    onReminderClick,
    activeReminderCommentId,
    onOpenReminderPopover,
    onCloseReminderPopover,
    renderReminderPopover,
}) => {
    const { t: translate } = useTranslation('tasks');
    const isAuthor = currentUserId != null && currentUserId === c.author_id;
    const reactions = typeof c.reactions === 'object' && c.reactions !== null ? c.reactions : {};
    const likes = Object.keys(reactions).length;
    const userLiked = currentUserId != null && reactions[currentUserId];
    const isSuggestion = Boolean(c.metadata?.is_suggestion || c.metadata?.structured || c.metadata?.type === 'suggestion');
    const isTaskRef = Boolean(c.metadata?.is_task_ref || c.metadata?.type === 'task_ref');

    return (
        <div className={`comment-item ${isSuggestion ? 'comment-item-suggestion' : ''} ${isTaskRef ? 'comment-item-task-ref' : ''}`} id={`task-comment-${c.comment_id}`}>
            <div className="comment-avatar">
                <img 
                    src={c.author_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${c.author_name || 'U'}`} 
                    alt={c.author_name} 
                    loading="lazy"
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${c.author_name || 'U'}`; }}
                />
            </div>
            <div className="comment-content">
                <div className="comment-meta">
                    <span className="comment-user">{c.author_name}</span>
                    <span className="comment-date">
                        {new Date(c.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                    </span>
                    {c.metadata?.response_time && (
                        <span className="comment-response-time-tag" style={{
                            marginLeft: '6px',
                            padding: '2px 6px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            color: 'rgba(255,255,255,0.4)',
                            borderRadius: '4px',
                            fontSize: '9px',
                            fontWeight: '500',
                            letterSpacing: '0.2px'
                        }}>
                            {(() => {
                                const ms = c.metadata.response_time;
                                const mins = Math.floor(ms / 60000);
                                if (mins < 60) return `${mins} min`;
                                const hours = Math.floor(mins / 60);
                                if (hours < 24) return `${hours} hora${hours > 1 ? 's' : ''}`;
                                const days = Math.floor(hours / 24);
                                if (days < 7) return `${days} dia${days > 1 ? 's' : ''}`;
                                const weeks = Math.floor(days / 7);
                                return `${weeks} semans`;
                            })()} after tag
                        </span>
                    )}
                    {isSuggestion && (
                        <span className="comment-suggestion-badge">
                            <Sparkles size={10} /> {translate('comments.suggestionBadge')}
                        </span>
                    )}
                    {c.metadata?.reminder && (
                        <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                            <span
                                className="comment-smart-tag-badge"
                                style={{ backgroundColor: 'hsla(150, 85%, 70%, 0.3)', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)' }}
                                onMouseEnter={(e) => onReminderMouseEnter(e, c.comment_id, c.metadata.reminder)}
                                onMouseLeave={onReminderMouseLeave}
                                onClick={(e) => onReminderClick(e, c.metadata.reminder)}
                                title={typeof c.metadata.reminder === 'object' && c.metadata.reminder.eventId ? translate('comments.reminderCopyTitle') : translate('comments.reminderTitle')}
                            >
                                <AlarmClock size={12} />
                            </span>
                            {hoveredReminderId === c.comment_id && hoveredReminderData && (
                                createPortal(
                                    <div style={{
                                        position: 'fixed',
                                        top: reminderTooltipPos.top,
                                        left: reminderTooltipPos.left,
                                        backgroundColor: '#1a1a1a',
                                        color: '#fff',
                                        padding: 'var(--space-2) var(--space-3)',
                                        borderRadius: 'var(--radius-md)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        zIndex: 999999,
                                        fontSize: '11px',
                                        pointerEvents: 'none',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <div style={{ fontWeight: '600', marginBottom: 'var(--space-1)', color: '#10b981' }}>{translate('comments.reminderDetailsTitle')}</div>
                                        <div><span style={{ color: '#888' }}>{translate('comments.reminderCalendar')}</span> {hoveredReminderData.calendar_name || translate('comments.reminderUnknownCalendar')}</div>
                                        <div><span style={{ color: '#888' }}>{translate('comments.reminderDate')}</span> {new Date(hoveredReminderData.start_time).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                                        <div style={{ marginTop: 'var(--space-1)', fontStyle: 'italic', color: '#666', fontSize: '9px' }}>{translate('comments.reminderCopyHint')}</div>
                                    </div>,
                                    document.body
                                )
                            )}
                        </div>
                    )}
                </div>

                {isTaskRef && (
                    <div style={{ marginBottom: '4px', marginTop: '2px' }}>
                        <span className="comment-task-ref-badge">
                            <LayoutList size={10} /> {translate('comments.linkedTaskBadge')}
                        </span>
                    </div>
                )}

                {isEditing ? renderCommentEditor() : (
                    isTaskRef ? (
                        <div 
                            className="task-ref-content comment-text" 
                            style={{ cursor: 'pointer', marginTop: '2px' }}
                            onClick={() => {
                                if (!c.metadata.ref_task_id) {
                                    console.error('Este enlace está corrupto o es de una versión anterior y no tiene el ID de la tarea destino.');
                                    return;
                                }
                                const spaceNick = window.location.pathname.split('/')[1] || 'frqtal';
                                window.open(`/${spaceNick}/${c.metadata.ref_task_id}`, '_blank');
                            }}
                        >
                            <div style={{ color: '#e5e5e5', fontSize: '11px', marginBottom: '4px' }}>{c.metadata.title}</div>
                            <div style={{ color: '#a3a3a3', fontSize: '11px' }}>
                                {c.metadata.description?.slice(0, 20)}
                                {(c.metadata.description?.length || 0) > 20 ? '...' : ''}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888', fontSize: '11px' }}>
                                    <AlarmClock size={12} /> {c.metadata.duration || 60} min
                                </div>
                                {c.metadata.assignee && (() => {
                                    const rawAssignee = c.metadata.assignee;
                                    const isObj = typeof rawAssignee === 'object' && rawAssignee !== null;
                                    
                                    const assigneeUser = usersToSelectFrom.find(u => {
                                        if (isObj && rawAssignee.id && String(u.id) === String(rawAssignee.id)) return true;
                                        
                                        const lookupStr = String(isObj ? (rawAssignee.nick || rawAssignee.username || rawAssignee.name || rawAssignee.id || '') : rawAssignee).toLowerCase().trim();
                                        return String(u.id) === lookupStr || 
                                               (u.nick && String(u.nick).toLowerCase().trim() === lookupStr) || 
                                               (u.username && String(u.username).toLowerCase().trim() === lookupStr) ||
                                               (u.name && String(u.name).toLowerCase().trim() === lookupStr);
                                    });
                                    const assigneeName = assigneeUser ? assigneeUser.nick || assigneeUser.username || assigneeUser.name : (isObj ? rawAssignee.name || 'Usuario' : rawAssignee);
                                    const fallbackSeed = isObj ? (rawAssignee.nick || rawAssignee.username || rawAssignee.name || rawAssignee.id || 'U') : rawAssignee;
                                    const assigneeAvatar = assigneeUser?.profile_picture || assigneeUser?.['link-profile-img-thumb'] || assigneeUser?.['link-profile-img'] || assigneeUser?.avatar || assigneeUser?.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${fallbackSeed}`;
                                    return (
                                        <img src={assigneeAvatar} title={assigneeName} style={{ width: '18px', height: '18px', minWidth: '18px', minHeight: '18px', borderRadius: '50%', backgroundColor: '#333', objectFit: 'cover', flexShrink: 0, aspectRatio: '1/1' }} />
                                    );
                                })()}
                            </div>
                        </div>
                    ) : (
                    <div
                        className="comment-text"
                        onClick={(e) => {
                            // Fix: el candado de un adjunto vive dentro de un <a href> (archivos)
                            // o <span> (imágenes/video) armado por el backend -- sin interceptar
                            // este click primero, el <a> navegaba al link firmado del archivo
                            // (se sentía como "compartir el link" en vez de togglear privacidad).
                            const toggleBtn = e.target.closest('.cy-embed-toggle');
                            if (toggleBtn) {
                                e.preventDefault();
                                e.stopPropagation();
                                const wrapper = toggleBtn.closest('[data-media-key]');
                                if (!wrapper) return;
                                const mediaKey = wrapper.dataset.mediaKey;
                                const currentAccess = wrapper.dataset.access;
                                const newAccess = currentAccess === 'private' ? 'public' : 'private';
                                onToggleMediaVisibility(c, mediaKey, newAccess, wrapper, toggleBtn);
                                return;
                            }
                            const expandBtn = e.target.closest('.expand-img-btn');
                            if (expandBtn) {
                                const url = expandBtn.getAttribute('data-url');
                                if (url) onExpandImage(url);
                                return;
                            }
                            const img = e.target.closest('img');
                            if (img && !toggleBtn) {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(img.src, '_blank');
                                return;
                            }
                        }}
                        dangerouslySetInnerHTML={{ __html: (c.content || '').replace(/<img([^>]*)src="([^"]+)"([^>]*)>/gi, (match, before, url, after) => {
                            if (match.includes('comment-img-wrapper')) return match;
                            if (!match.includes('max-width') && !match.includes('display: block')) return match; // skip avatars
                            const expandSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>`;
                            return `<div class="comment-img-wrapper" style="position: relative; display: inline-block; max-width: 100%; margin: 8px 0;" contenteditable="false">
                                <img${before}src="${url}"${after} style="max-width: 100%; height: auto; border-radius: 8px; display: block; cursor: pointer;" class="comment-img-expandable" />
                                <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 40px; background: linear-gradient(to top, rgba(0,0,0,0.7), transparent); border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; display: flex; align-items: flex-end; justify-content: flex-end; padding: 8px; pointer-events: none;">
                                    <div class="expand-img-btn" data-url="${url}" style="pointer-events: auto; cursor: pointer; color: white; background: rgba(0,0,0,0.5); border-radius: 4px; padding: 4px; display: flex; align-items: center; justify-content: center;" title="${translate('comments.expandImage')}">
                                        ${expandSvg}
                                    </div>
                                </div>
                            </div>`;
                        }) }}
                    />
                    )
                )}

                {!isEditing && (
                    <div className="comment-actions">
                        {/* Read Receipts Avatars */}
                        {Array.isArray(c.reads) && c.reads.filter(r => String(r.user_id) !== String(c.author_id)).length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 'auto' }}>
                                {c.reads
                                    .filter(r => String(r.user_id) !== String(c.author_id))
                                    .slice(0, 5)
                                    .map((read, idx) => (
                                    <div key={read.user_id} style={{ 
                                        position: 'relative', 
                                        marginLeft: idx === 0 ? '0' : '-6px', 
                                        zIndex: 5 - idx 
                                    }}
                                    className="read-receipt-avatar-wrapper"
                                    title={`${read.nick} leyó hace ${(() => {
                                        const ms = new Date(read.read_at).getTime() - new Date(c.created_at).getTime();
                                        const mins = Math.floor(Math.max(0, ms) / 60000);
                                        if (mins < 60) return `${mins} min`;
                                        const hours = Math.floor(mins / 60);
                                        if (hours < 24) return `${hours} hora${hours > 1 ? 's' : ''}`;
                                        const days = Math.floor(hours / 24);
                                        if (days < 7) return `${days} dia${days > 1 ? 's' : ''}`;
                                        const weeks = Math.floor(days / 7);
                                        return `${weeks} semans`;
                                    })()}`}
                                    >
                                        <img 
                                            src={read.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${read.nick || 'User'}`} 
                                            alt={read.nick} 
                                            onError={(e) => { e.target.onerror = null; e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${read.nick || 'User'}`; }}
                                            style={{
                                                width: '14px', 
                                                height: '14px', 
                                                borderRadius: '50%', 
                                                border: '1px solid #1a1a1a', 
                                                objectFit: 'cover',
                                                display: 'block'
                                            }} 
                                        />
                                        <div className="read-receipt-tooltip" style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            marginBottom: '4px',
                                            backgroundColor: '#000',
                                            color: '#fff',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontSize: '9px',
                                            whiteSpace: 'nowrap',
                                            pointerEvents: 'none',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            zIndex: 10
                                        }}>
                                            {(() => {
                                                const ms = new Date(read.read_at).getTime() - new Date(c.created_at).getTime();
                                                const mins = Math.floor(Math.max(0, ms) / 60000);
                                                if (mins < 60) return `${mins} min`;
                                                const hours = Math.floor(mins / 60);
                                                if (hours < 24) return `${hours} hora${hours > 1 ? 's' : ''}`;
                                                const days = Math.floor(hours / 24);
                                                if (days < 7) return `${days} dia${days > 1 ? 's' : ''}`;
                                                const weeks = Math.floor(days / 7);
                                                return `${weeks} semans`;
                                            })()}
                                        </div>
                                    </div>
                                ))}
                                {c.reads.filter(r => String(r.user_id) !== String(c.author_id)).length > 5 && (
                                    <div style={{
                                        width: '14px', height: '14px', borderRadius: '50%',
                                        backgroundColor: '#333', color: '#fff', fontSize: '8px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        marginLeft: '-6px', zIndex: 0, border: '1px solid #1a1a1a'
                                    }}>
                                        +{c.reads.filter(r => String(r.user_id) !== String(c.author_id)).length - 5}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className={`reaction-btn ${userLiked ? 'active' : ''}`} onClick={() => onToggleReaction(c.comment_id)} aria-label={userLiked ? translate('comments.unlikeAria') : translate('comments.likeAria')}>
                            <Heart size={12} fill={userLiked ? "#ef4444" : "none"} color={userLiked ? "#ef4444" : "#666"} />
                            {likes > 0 && <span>{likes}</span>}
                        </div>

                        <div
                            className="reaction-btn"
                            title={translate('comments.addReminderTitle')}
                            aria-label={translate('comments.addReminderTitle')}
                            onClick={(e) => onOpenReminderPopover(c.comment_id, e)}
                        >
                            <AlarmClock size={12} color={c.metadata?.reminder ? "#10b981" : "#666"} />
                        </div>
                        {isAuthor && (
                            <>
                                {!isTaskRef && (
                                    <div
                                        className="edit-btn"
                                        onClick={() => onStartEdit(c.comment_id)}
                                        aria-label={translate('comments.editAria')}
                                    >
                                        <Pencil size={12} color="#666" />
                                    </div>
                                )}
                                <div className="edit-btn" onClick={() => onDeleteComment(c.comment_id)} title={translate('comments.delete')} aria-label={translate('comments.deleteAria')}>
                                    <Trash2 size={12} color="#666" />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Inline Reminder Popover for this comment */}
                {activeReminderCommentId === c.comment_id && renderReminderPopover(onCloseReminderPopover)}
            </div>
        </div>
    );
};

export default CommentItem;
